import React, { useState } from 'react';
import {
  Card,
  Button,
  Input,
  Table,
  message,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  Modal,
  Tag,
  Descriptions,
} from 'antd';
import { ReloadOutlined, DeleteOutlined, PlayCircleOutlined, SearchOutlined, RedoOutlined } from '@ant-design/icons';
import * as adminApi from '@apis/admin';
import { useQuery, useMutation } from '@tanstack/react-query';

const { Title, Text } = Typography;

interface MessageInfo {
  messageId: string;
  messageContent?: any;
  processingAt?: string;
  processedAt?: string;
}

const SqsMessageManagementPage: React.FC = () => {
  const [searchMessageId, setSearchMessageId] = useState<string>('');
  const [selectedMessageId, setSelectedMessageId] = useState<string>('');

  // 전체 통계 조회
  const {
    data: statistics,
    isLoading: isLoadingStatistics,
    refetch: refetchStatistics,
  } = useQuery({
    queryKey: ['sqs-statistics'],
    queryFn: () => adminApi.getSqsMessageStatus(),
    refetchInterval: 5000, // 5초마다 자동 새로고침
  });

  // 특정 메시지 상태 조회
  const {
    data: messageStatus,
    isLoading: isLoadingMessageStatus,
    refetch: refetchMessageStatus,
  } = useQuery({
    queryKey: ['sqs-message-status', selectedMessageId],
    queryFn: () => adminApi.getSqsMessageStatus(selectedMessageId),
    enabled: !!selectedMessageId,
    refetchInterval: 2000, // 2초마다 자동 새로고침
  });

  // 막힌 메시지 정리
  const clearStuckMessagesMutation = useMutation({
    mutationFn: adminApi.clearStuckSqsMessages,
    onSuccess: () => {
      message.success('막힌 메시지가 정리되었습니다.');
      refetchStatistics();
    },
    onError: (error: any) => {
      message.error(`작업 실패: ${error.response?.data?.message || error.message}`);
    },
  });

  // 메시지 강제 재처리
  const forceReprocessMutation = useMutation({
    mutationFn: adminApi.forceReprocessSqsMessage,
    onSuccess: () => {
      message.success('메시지가 재처리 대기 상태로 변경되었습니다.');
      refetchStatistics();
      if (selectedMessageId) {
        refetchMessageStatus();
      }
    },
    onError: (error: any) => {
      message.error(`작업 실패: ${error.response?.data?.message || error.message}`);
    },
  });

  // Redis에서 메시지 재처리
  const reprocessFromRedisMutation = useMutation({
    mutationFn: adminApi.reprocessSqsMessageFromRedis,
    onSuccess: () => {
      message.success('메시지가 Redis에서 재처리되었습니다.');
      refetchStatistics();
      if (selectedMessageId) {
        refetchMessageStatus();
      }
    },
    onError: (error: any) => {
      message.error(`작업 실패: ${error.response?.data?.message || error.message}`);
    },
  });

  const handleSearchMessage = () => {
    if (!searchMessageId.trim()) {
      message.warning('메시지 ID를 입력해주세요.');
      return;
    }
    setSelectedMessageId(searchMessageId.trim());
  };

  const handleClearStuckMessages = () => {
    Modal.confirm({
      title: '막힌 메시지 정리',
      content: '오래된 처리 중 상태의 메시지들을 정리하시겠습니까?',
      onOk: () => {
        clearStuckMessagesMutation.mutate();
      },
    });
  };

  const handleForceReprocess = (messageId: string) => {
    Modal.confirm({
      title: '메시지 강제 재처리',
      content: `메시지 ID: ${messageId}를 강제로 재처리하시겠습니까?`,
      onOk: () => {
        forceReprocessMutation.mutate(messageId);
      },
    });
  };

  const handleReprocessFromRedis = (messageId: string) => {
    Modal.confirm({
      title: 'Redis에서 메시지 재처리',
      content: `메시지 ID: ${messageId}를 Redis에 저장된 내용으로 재처리하시겠습니까?`,
      onOk: () => {
        reprocessFromRedisMutation.mutate(messageId);
      },
    });
  };

  const getMessageTypeTag = (messageContent: any) => {
    if (!messageContent?.type) return null;

    const typeColors: { [key: string]: string } = {
      user_behavior: 'blue',
      policy_violation_chat_message: 'red',
      kakao_alimtalk: 'green',
      push_notification: 'purple',
      search_keyword: 'orange',
      user_point_notification: 'cyan',
    };

    return <Tag color={typeColors[messageContent.type] || 'default'}>{messageContent.type}</Tag>;
  };

  const renderMessageContent = (messageContent: any) => {
    if (!messageContent) return <Text type="secondary">내용 없음</Text>;

    return (
      <Descriptions size="small" column={1}>
        <Descriptions.Item label="타입">{getMessageTypeTag(messageContent)}</Descriptions.Item>
        <Descriptions.Item label="ID">{messageContent.id}</Descriptions.Item>
        {messageContent.data && (
          <Descriptions.Item label="데이터">
            <pre style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
              {JSON.stringify(messageContent.data, null, 2)}
            </pre>
          </Descriptions.Item>
        )}
      </Descriptions>
    );
  };

  // 처리 중인 메시지 테이블 컬럼
  const processingColumns = [
    {
      title: '메시지 ID',
      dataIndex: 'messageId',
      key: 'messageId',
      render: (messageId: string) => (
        <Text copyable={{ text: messageId }} style={{ fontFamily: 'monospace' }}>
          {messageId}
        </Text>
      ),
    },
    {
      title: '메시지 타입',
      dataIndex: 'messageContent',
      key: 'messageType',
      render: (messageContent: any) => getMessageTypeTag(messageContent),
    },
    {
      title: '처리 시작 시간',
      dataIndex: 'processingAt',
      key: 'processingAt',
      render: (processingAt: string) => (processingAt ? new Date(processingAt).toLocaleString() : '-'),
    },
    {
      title: '액션',
      key: 'action',
      render: (record: MessageInfo) => (
        <Space>
          <Button size="small" icon={<SearchOutlined />} onClick={() => setSelectedMessageId(record.messageId)}>
            상세 조회
          </Button>
          <Button size="small" icon={<PlayCircleOutlined />} onClick={() => handleForceReprocess(record.messageId)}>
            재처리
          </Button>
          {record.messageContent && (
            <Button
              size="small"
              icon={<RedoOutlined />}
              onClick={() => handleReprocessFromRedis(record.messageId)}
              type="primary">
              Redis 재처리
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // 처리 완료된 메시지 테이블 컬럼
  const processedColumns = [
    {
      title: '메시지 ID',
      dataIndex: 'messageId',
      key: 'messageId',
      render: (messageId: string) => (
        <Text copyable={{ text: messageId }} style={{ fontFamily: 'monospace' }}>
          {messageId}
        </Text>
      ),
    },
    {
      title: '메시지 타입',
      dataIndex: 'messageContent',
      key: 'messageType',
      render: (messageContent: any) => getMessageTypeTag(messageContent),
    },
    {
      title: '처리 완료 시간',
      dataIndex: 'processedAt',
      key: 'processedAt',
      render: (processedAt: string) => (processedAt ? new Date(processedAt).toLocaleString() : '-'),
    },
    {
      title: '액션',
      key: 'action',
      render: (record: MessageInfo) => (
        <Space>
          <Button size="small" icon={<SearchOutlined />} onClick={() => setSelectedMessageId(record.messageId)}>
            상세 조회
          </Button>
          {record.messageContent && (
            <Button
              size="small"
              icon={<RedoOutlined />}
              onClick={() => handleReprocessFromRedis(record.messageId)}
              type="primary">
              Redis 재처리
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const processingData = statistics?.data?.processingMessages || [];
  const processedData = statistics?.data?.processedMessages || [];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>SQS 메시지 관리</Title>

      {/* 통계 카드 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="처리 중인 메시지"
              value={statistics?.data?.processingCount || 0}
              valueStyle={{ color: '#1890ff' }}
              suffix={
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => refetchStatistics()}
                  loading={isLoadingStatistics}
                />
              }
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="처리 완료된 메시지"
              value={statistics?.data?.processedCount || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={handleClearStuckMessages}
              loading={clearStuckMessagesMutation.isPending}
              block>
              막힌 메시지 정리
            </Button>
          </Card>
        </Col>
      </Row>

      {/* 메시지 검색 */}
      <Card title="메시지 상태 조회" style={{ marginBottom: '24px' }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="메시지 ID 입력"
            value={searchMessageId}
            onChange={(e) => setSearchMessageId(e.target.value)}
            onPressEnter={handleSearchMessage}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearchMessage}
            loading={isLoadingMessageStatus}>
            조회
          </Button>
        </Space.Compact>

        {/* 메시지 상태 결과 */}
        {selectedMessageId && messageStatus && (
          <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Title level={4}>메시지 ID: {selectedMessageId}</Title>
            <Row gutter={16}>
              <Col span={6}>
                <Text strong>처리 중: </Text>
                <Text style={{ color: messageStatus.data.processing ? '#1890ff' : '#666' }}>
                  {messageStatus.data.processing ? '예' : '아니오'}
                </Text>
              </Col>
              <Col span={6}>
                <Text strong>처리 완료: </Text>
                <Text style={{ color: messageStatus.data.processed ? '#52c41a' : '#666' }}>
                  {messageStatus.data.processed ? '예' : '아니오'}
                </Text>
              </Col>
              <Col span={12}>
                <Space>
                  <Button
                    size="small"
                    icon={<PlayCircleOutlined />}
                    onClick={() => handleForceReprocess(selectedMessageId)}
                    loading={forceReprocessMutation.isPending}>
                    재처리
                  </Button>
                  {messageStatus.data.messageContent && (
                    <Button
                      size="small"
                      icon={<RedoOutlined />}
                      onClick={() => handleReprocessFromRedis(selectedMessageId)}
                      loading={reprocessFromRedisMutation.isPending}
                      type="primary">
                      Redis 재처리
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
            {messageStatus.data.processingAt && (
              <div style={{ marginTop: '8px' }}>
                <Text strong>처리 시작 시간: </Text>
                <Text>{new Date(messageStatus.data.processingAt).toLocaleString()}</Text>
              </div>
            )}
            {messageStatus.data.processedAt && (
              <div style={{ marginTop: '8px' }}>
                <Text strong>처리 완료 시간: </Text>
                <Text>{new Date(messageStatus.data.processedAt).toLocaleString()}</Text>
              </div>
            )}
            {messageStatus.data.messageContent && (
              <div style={{ marginTop: '16px' }}>
                <Title level={5}>메시지 내용:</Title>
                {renderMessageContent(messageStatus.data.messageContent)}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* 처리 중인 메시지 목록 */}
      <Card title="처리 중인 메시지" style={{ marginBottom: '24px' }}>
        <Table
          columns={processingColumns}
          dataSource={processingData}
          loading={isLoadingStatistics}
          pagination={{ pageSize: 10 }}
          size="small"
          rowKey="messageId"
        />
      </Card>

      {/* 처리 완료된 메시지 목록 */}
      <Card title="처리 완료된 메시지 (최근 100개)">
        <Table
          columns={processedColumns}
          dataSource={processedData.slice(0, 100)}
          loading={isLoadingStatistics}
          pagination={{ pageSize: 10 }}
          size="small"
          rowKey="messageId"
        />
      </Card>
    </div>
  );
};

export default SqsMessageManagementPage;
